import type { MerchantAccountStatus } from "@funded-here-interface/common/src/schemas/merchantAccount";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import getMerchantAccountStatus from "../services/getMerchantAccountStatus";
import { RootState } from "../store";

/**
 * Poll interval for the merchant waiting pages — the same 15s cadence as the
 * investor waiting page, kept independent so neither feature's hook depends on
 * the other's.
 */
export const MERCHANT_ACCOUNT_POLL_MS = 15_000;

interface UseMerchantAccountStatusOptions {
  /** Skip the query entirely (e.g. this thanks page is not a merchant waiting page). */
  enabled?: boolean;
  /** Re-fetch on an interval — used by the waiting pages, not by the gate. */
  poll?: boolean;
}

/**
 * Reads the server-computed merchant portal verdict: onboarding complete and
 * the Wallex collection account active.
 *
 * Unlike the investor verdict this is not on the profile response, so the gate
 * and the waiting pages share this one query key — when a waiting page sees
 * `isActive` flip, the gate it releases into reads the same cached answer.
 *
 * The gate (a non-polling caller) never re-asks once it has an answer:
 * activation is one-way and the waiting pages own polling. A stale re-check on
 * tab focus would otherwise eject an admitted merchant whenever the payment
 * service blips, because the backend fails safe to `isActive: false`. With no
 * expiry, `userId` in the key is what stops a cached verdict outliving its user.
 */
function useMerchantAccountStatus({
  enabled = true,
  poll = false,
}: UseMerchantAccountStatusOptions = {}) {
  const token = useSelector((state: RootState) => state.auth.token);
  const userId = useSelector((state: RootState) => state.auth.userId);

  return useQuery<MerchantAccountStatus>({
    queryKey: ["merchant-account-status", userId],
    queryFn: () => getMerchantAccountStatus({ token }),
    enabled: enabled && Boolean(token) && token !== "none",
    retry: false,
    refetchInterval: poll ? MERCHANT_ACCOUNT_POLL_MS : false,
    staleTime: poll ? MERCHANT_ACCOUNT_POLL_MS : Infinity,
  });
}

export default useMerchantAccountStatus;
